import { ReactNode, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  MessageSquare, 
  Home, 
  Settings, 
  Code, 
  LogOut, 
  Menu, 
  X, 
  BarChart,
  FileText,
  HotelIcon
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/lib/context/AuthContext';
import { getHotelData } from '@/lib/services/hotelService';
import { HotelData } from '@/lib/types/auth';

interface DashboardLayoutProps {
  children: ReactNode;
}

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const [hotelInfo, setHotelInfo] = useState<HotelData | null>(null);
  const location = useLocation();
  const { auth, signOut } = useAuth();
  
  useEffect(() => {
    const loadHotelData = async () => {
      if (auth.user) {
        try {
          const data = await getHotelData();
          if (data) {
            setHotelInfo(data);
          }
        } catch (error) {
          console.error('Error loading hotel data:', error);
        }
      }
    };

    loadHotelData();
  }, [auth.user]);
  
  const handleLogout = async () => {
    try {
      await signOut();
      toast.success("You've been logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Failed to log out");
    }
  };
  
  const isActive = (path: string) => {
    return location.pathname === path;
  };
  
  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Embed Code', href: '/dashboard/embed', icon: Code },
    { name: 'Fact Sheet', href: '/dashboard/factsheet', icon: FileText },
    { name: 'Analytics', href: '/dashboard/analytics', icon: BarChart },
    { name: 'Settings', href: '/dashboard/settings', icon: Settings },
  ];
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Nav */}
      <div className="lg:hidden bg-white border-b px-4 py-3 flex justify-between items-center">
        <Link to="/dashboard" className="flex items-center space-x-2">
          <MessageSquare className="h-5 w-5 text-hotel-primary" />
          <span className="font-bold text-hotel-primary">Lobier AI</span>
        </Link>
        <button 
          onClick={() => setCollapsed(!collapsed)}
          className="p-2 rounded-md text-gray-500 hover:bg-gray-100"
        >
          {collapsed ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      
      {/* Sidebar (Mobile) */}
      {collapsed && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setCollapsed(false)}>
          <div 
            className="h-full w-64 bg-white p-4 animate-slide-up" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6 flex justify-between items-center">
              <Link to="/dashboard" className="flex items-center space-x-2">
                <MessageSquare className="h-5 w-5 text-hotel-primary" />
                <span className="font-bold text-hotel-primary">Lobier AI</span>
              </Link>
              <button 
                onClick={() => setCollapsed(false)}
                className="p-1 rounded-md text-gray-500 hover:bg-gray-100"
              >
                <X size={18} />
              </button>
            </div>
            
            <nav className="space-y-1">
              <Link
                to="/dashboard"
                className={cn(
                  "flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium",
                  isActive('/dashboard') 
                    ? "bg-hotel-primary text-white" 
                    : "text-gray-700 hover:bg-gray-100"
                )}
                onClick={() => setCollapsed(false)}
              >
                <Home size={18} />
                <span>Dashboard</span>
              </Link>
              
              <Link
                to="/dashboard/settings"
                className={cn(
                  "flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium hidden",
                  isActive('/dashboard/settings') 
                    ? "bg-hotel-primary text-white" 
                    : "text-gray-700 hover:bg-gray-100"
                )}
                onClick={() => setCollapsed(false)}
              >
                <Settings size={18} />
                <span>Settings</span>
              </Link>
              
              <Link
                to="/dashboard/analytics"
                className={cn(
                  "flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium",
                  isActive('/dashboard/analytics') 
                    ? "bg-hotel-primary text-white" 
                    : "text-gray-700 hover:bg-gray-100"
                )}
                onClick={() => setCollapsed(false)}
              >
                <BarChart size={18} />
                <span>Analytics</span>
              </Link>
              
              <Link
                to="/dashboard/embed"
                className={cn(
                  "flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium",
                  isActive('/dashboard/embed') 
                    ? "bg-hotel-primary text-white" 
                    : "text-gray-700 hover:bg-gray-100"
                )}
                onClick={() => setCollapsed(false)}
              >
                <Code size={18} />
                <span>Embed Code</span>
              </Link>
            </nav>
            
            <div className="mt-8">
              <div className="px-3 py-2 mb-3">
                <span className="text-sm text-gray-600 font-medium">
                  {hotelInfo?.name}
                </span>
              </div>
              <Button 
                variant="outline" 
                className="w-full justify-start text-red-600 border-red-200"
                onClick={handleLogout}
              >
                <LogOut size={18} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex h-screen lg:overflow-hidden">
        {/* Sidebar (Desktop) */}
        <div className="hidden lg:block w-48 bg-hotel-primary text-white">
          <div className="h-full flex flex-col">
            <div className="p-4 mb-4">
              <Link to="/dashboard" className="flex items-center space-x-2">
                <MessageSquare className="h-6 w-6 text-green-500" />
                <span className="font-bold text-white">Lobier AI</span>
              </Link>
            </div>
            
            <nav className="flex-1 p-4 space-y-1">
              <Link
                to="/dashboard"
                className={cn(
                  "flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium",
                  isActive('/dashboard') 
                    ? "bg-sky-900 text-white" 
                    : "text-white hover:bg-sky-950"
                )}
              >
                <Home size={18} />
                <span>Dashboard</span>
              </Link>
              
              <Link
                to="/dashboard/settings"
                className={cn(
                  "flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium hidden",
                  isActive('/dashboard/settings') 
                    ? "bg-white text-hotel-primary" 
                    : "text-white hover:bg-sky-950"
                )}
              >
                <Settings size={18} />
                <span>Settings</span>
              </Link>
              
              <Link
                to="/dashboard/analytics"
                className={cn(
                  "flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium",
                  isActive('/dashboard/analytics') 
                    ? "bg-white text-hotel-primary" 
                    : "text-white hover:bg-sky-950"
                )}
              >
                <BarChart size={18} />
                <span>Analytics</span>
              </Link>
              
              <Link
                to="/dashboard/embed"
                className={cn(
                  "flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium",
                  isActive('/dashboard/embed') 
                    ? "bg-white text-hotel-primary" 
                    : "text-white hover:bg-sky-950"
                )}
              >
                <Code size={18} />
                <span>Embed Code</span>
              </Link>
            </nav>
            
            <div className="p-4">
              <div className="py-2 mb-3 flex items-center justify-center gap-1">
                <HotelIcon className="h-5 w-5 text-white" />
                <div className="text-xs text-white font-medium line-clamp-1">
                  {hotelInfo?.name}
                </div>
              </div>
              <Button 
                variant="outline" 
                className="w-full justify-start bg-sky-950 hover:bg-sky-900 hover:text-white border-none"
                onClick={handleLogout}
              >
                <LogOut size={18} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
        
        {/* Main Content */}

        <main className="flex-1 p-6 pt-3 overflow-auto shadow-lg bg-white">
          <header className="mb-4 flex items-center justify-between">
            <h1 className="text-sm mt-1 font-semibold text-hotel-primary">
              {location.pathname === '/dashboard' && 'Dashboard'}
              {location.pathname === '/dashboard/settings' && 'Settings'}
              {location.pathname === '/dashboard/analytics' && 'Analytics'}
              {location.pathname === '/dashboard/embed' && 'Embed Code'}
            </h1>
          </header>
          {children}
        </main>

      </div>
    </div>
  );
};

export default DashboardLayout;
